import { useQuery } from '@tanstack/react-query'
import { MetricPeriod } from '@/lib/utils'
import { analyticsService, AggregatedAnalyticsData } from '@/services/analyticsService'

export interface UseMultiStudioAnalyticsParams {
  studioIds: number[]
  currentPeriod: MetricPeriod | null
}

export interface UseMultiStudioAnalyticsReturn extends Omit<AggregatedAnalyticsData, 'analytics'> {
  analytics: AggregatedAnalyticsData['analytics'] | null
  loading: boolean
  error: string | null
  refresh: () => void
  /** true, когда батчевые raw-данные уже получены (или ошибка) */
  rawDataReady: boolean
  /** Сырые weeklyPlans из батчевого запроса (для пер-студийных диагностик) */
  rawWeeklyPlans: any[]
  /** Сырые periodTotals из батчевого запроса (для пер-студийных диагностик) */
  rawPeriodTotals: any[]
  /** Готовый агрегированный недельный план-факт из RPC (COALESCE+SUM на стороне БД) */
  rawAggregatedWeeklyPlans: any[]
}

const EMPTY_DAILY_DATA: AggregatedAnalyticsData['dailyData'] = []
const EMPTY_CONVERSION_DAILY_DATA: AggregatedAnalyticsData['conversionDailyData'] = []
const EMPTY_NEXT_PERIOD_HEAD_DATA: AggregatedAnalyticsData['nextPeriodHeadData'] = []
const EMPTY_FUNNEL_DATA: AggregatedAnalyticsData['funnelData'] = {
  leads: 0,
  appointments: 0,
  visits: 0,
  sales: 0
}
const EMPTY_STUDIO_IDS: number[] = []
const EMPTY_RAW_DATA: any[] = []

/**
 * Hook для загрузки и агрегации данных аналитики нескольких студий
 * Параллельно загружает данные всех студий и агрегирует результаты
 * 
 * @example
 * const { analytics, dailyData, loading } = useMultiStudioAnalytics({
 *   studioIds: [1, 2, 3],
 *   currentPeriod: period
 * })
 */
export function useMultiStudioAnalytics({
  studioIds,
  currentPeriod
}: UseMultiStudioAnalyticsParams): UseMultiStudioAnalyticsReturn {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<AggregatedAnalyticsData, Error>({
    queryKey: ['multi-studio-analytics', [...studioIds].sort().join(','), currentPeriod?.period_id],
    queryFn: async () => {
      if (!currentPeriod) {
        throw new Error('No current period provided')
      }
      if (studioIds.length === 0) {
        throw new Error('No studios provided')
      }
      return analyticsService.loadMultiStudioAnalytics(studioIds, currentPeriod)
    },
    enabled: !!currentPeriod && studioIds.length > 0,
    staleTime: 5 * 60 * 1000, // Кэш на 5 минут
    gcTime: 10 * 60 * 1000, // Хранить в памяти 10 минут
  })

  // Возвращаем данные в формате, совместимом с useStudioAnalytics
  return {
    analytics: data?.analytics || null,
    dailyData: data?.dailyData || EMPTY_DAILY_DATA,
    conversionDailyData: data?.conversionDailyData || EMPTY_CONVERSION_DAILY_DATA,
    nextPeriodHeadData: data?.nextPeriodHeadData || EMPTY_NEXT_PERIOD_HEAD_DATA,
    planData: data?.planData || null,
    previousPeriodData: data?.previousPeriodData || null,
    funnelData: data?.funnelData || EMPTY_FUNNEL_DATA,
    studiosCount: data?.studiosCount || 0,
    studioIds: data?.studioIds || EMPTY_STUDIO_IDS,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refresh: () => { refetch() },
    rawDataReady: !!data,
    rawWeeklyPlans: (data as any)?.rawWeeklyPlans || EMPTY_RAW_DATA,
    rawPeriodTotals: (data as any)?.rawPeriodTotals || EMPTY_RAW_DATA,
    rawAggregatedWeeklyPlans: (data as any)?.rawAggregatedWeeklyPlans || EMPTY_RAW_DATA
  }
}
